import React, { useMemo, useState, useEffect } from 'react';
import { FlightDetails } from '../interface/flight.details';
import Destination from './Destination';
import './BaggageCarousel.css';
import FlipText from './FlipText';
import moment from 'moment';
import mahbLogo from '../assets/MAHB_LOGO.jpg';
import { BUCKET_URL } from '../utils/env-utils';
import { RemarkToCodeMapping } from '@industronics/fids-utils';
import type { Announcement } from '../interface/device.details';
import ScrollingText from './scrolling/ScrollingText';

const LandedIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 640 550" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="620" height="530" rx="40" fill="#FFD700" />
        <rect x="30" y="30" width="580" height="490" rx="30" fill="#FFD700" stroke="#000" strokeWidth="8" />
        <svg x="120" y="55" width="400" height="390" viewBox="0 0 640 550">
            <path fill="#000" d="M418.6 257.1L297.9 67.7C293.1 60.1 285.3 54.9 276.5 53.4L233.4 45.8C222.6 43.9 213.2 53.1 214.8 63.9L238.8 225.5L133.8 207L100 145.2C96.5 138.7 90.2 134.2 83.1 133L66 130C56.2 128.3 47.2 135.9 47.2 145.8L47.8 252.1C48 283 70.2 309.4 100.7 314.8L114.2 317.2L114.2 317.2L531.8 390.8C562.3 396.2 591.3 375.8 596.7 345.4C602.1 315 581.7 285.9 551.3 280.5L418.6 257.1zM256 448C273.7 448 288 433.7 288 416C288 398.3 273.7 384 256 384C238.3 384 224 398.3 224 416C224 433.7 238.3 448 256 448zM387.2 432.7C387.2 415 372.9 400.7 355.2 400.7C337.5 400.7 323.2 415 323.2 432.7C323.2 450.4 337.5 464.7 355.2 464.7C372.9 464.7 387.2 450.4 387.2 432.7zM64 512C46.3 512 32 526.3 32 544C32 561.7 46.3 576 64 576L576 576C593.7 576 608 561.7 608 544C608 526.3 593.7 512 576 512L64 512z"/>
        </svg>
    </svg>
);

const SuitcaseIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 640 550" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="620" height="530" rx="40" fill="#FFD700" />
        <rect x="30" y="30" width="580" height="490" rx="30" fill="#FFD700" stroke="#000" strokeWidth="8" />
        <svg x="120" y="45" width="400" height="410" viewBox="0 0 640 550">
            <path fill="#000" d="M264 112L376 112C380.4 112 384 115.6 384 120L384 160L256 160L256 120C256 115.6 259.6 112 264 112zM208 120L208 544L432 544L432 120C432 89.1 406.9 64 376 64L264 64C233.1 64 208 89.1 208 120zM480 160L480 544L512 544C547.3 544 576 515.3 576 480L576 224C576 188.7 547.3 160 512 160L480 160zM160 544L160 160L128 160C92.7 160 64 188.7 64 224L64 480C64 515.3 92.7 544 128 544L160 544z"/>
        </svg>
    </svg>
);

const ClosedIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 640 550" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="620" height="530" rx="40" fill="#FFD700" />
        <rect x="30" y="30" width="580" height="490" rx="30" fill="#FFD700" stroke="#000" strokeWidth="8" />
        <svg x="80" y="30" width="480" height="460" viewBox="0 0 640 640">
            <path fill="#000" d="M264 112L376 112C380.4 112 384 115.6 384 120L384 160L256 160L256 120C256 115.6 259.6 112 264 112zM208 120L208 160L128 160C92.7 160 64 188.7 64 224L64 320L369 320C402.8 290.1 447.3 272 496 272C524.6 272 551.6 278.2 576 289.4L576 224C576 188.7 547.3 160 512 160L432 160L432 120C432 89.1 406.9 64 376 64L264 64C233.1 64 208 89.1 208 120zM288 416C270.3 416 256 401.7 256 384L256 368L64 368L64 480C64 515.3 92.7 544 128 544L321.4 544C310.2 519.6 304 492.6 304 464C304 447.4 306.1 431.3 310 416L288 416zM640 464C640 384.5 575.5 320 496 320C416.5 320 352 384.5 352 464C352 543.5 416.5 608 496 608C575.5 608 640 543.5 640 464z"/>
            <line x1="456" y1="424" x2="536" y2="504" stroke="#FFD700" strokeWidth="24" strokeLinecap="round" />
            <line x1="536" y1="424" x2="456" y2="504" stroke="#FFD700" strokeWidth="24" strokeLinecap="round" />
        </svg>
    </svg>
);

const getRemarkIcon = (key: string) => {
    switch (key) {
        case RemarkToCodeMapping.LANDED:
            return <LandedIcon className="bc-icon-svg" />;
        case RemarkToCodeMapping.CAROUSEL_OPEN:
        case RemarkToCodeMapping.FIRST_BAG:
        case RemarkToCodeMapping.LAST_BAG:
            return <SuitcaseIcon className="bc-icon-svg" />;
        case RemarkToCodeMapping.CAROUSEL_CLOSED:
            return <ClosedIcon className="bc-icon-svg" />;
        default:
            return null;
    }
};

interface BaggageCarouselProps {
    flights: FlightDetails[];
    totalRows: number;
    carouselName?: string;
    announcement?: Announcement;
}

const BaggageRow: React.FC<{
    flight: FlightDetails | null;
    index: number;
    tick: number;
}> = ({ flight, index, tick }) => {

    const remarksList = useMemo<string[]>(() => {
        if (!flight) return [];
        return Array.isArray(flight?.carouselRemarks?.translations)
      ? flight.carouselRemarks.translations.length ? flight.carouselRemarks.translations.map((item: { value: any }) => item.value) : [flight.carouselRemarks.description]
      : []
    }, [flight?.carouselRemarks?.translations, flight?.carouselRemarks?.key]);

    const scheduledDisplay = useMemo(() => {
        if (!flight?.scheduledTravelTime) return { time: '', date: '' };
        const scheduled = moment(flight.scheduledTravelTime).utcOffset(8);
        return {
            time: scheduled.format('HH:mm'),
            date: scheduled.format('DD MMM'),
        };
    }, [flight?.scheduledTravelTime]);

    return (
        <div className={`bc-row ${index % 2 === 0 ? 'bc-row--even' : 'bc-row--odd'}`}>
            {flight ? (
                <div className="bc-inner">

                    {/* COL 1: Airline Logo */}
                    <div className="bc-col bc-col--logo">
                        <img
                            className="bc-logo-img"
                            src={`${BUCKET_URL}/0350x0090/${flight.flightNumber.split(' ')[0]}.bmp`}
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                    `${BUCKET_URL}/0350x0090/${flight.flightNumber.split(' ')[0]}.jpg`;
                            }}
                            alt="Airline Logo"
                        />
                    </div>

                    {/* COL 3: Flight number (top) + Station (bottom) */}
                    <div className="bc-col bc-col--info">
                        <span className="bc-flight-number">{flight.flightNumber}</span>
                        <div className="bc-flight-station">
                            <Destination className='bc-dest-text' flight={flight} tick={tick} />
                        </div>
                    </div>

                    {/* COL 3: Remark Icon */}
                    <div className="bc-col bc-col--icons">
                        {flight.carouselRemarks?.key && getRemarkIcon(flight.carouselRemarks.key)}
                    </div>

                    {/* COL 4: Remark */}
                    <div className="bc-col bc-col--remark">
                        <FlipText className='bc-remark-text' dataList={remarksList} tick={tick} />
                    </div>

                </div>
            ) : (
                <div className="bc-empty" />
            )}
        </div>
    );
};

const BaggageCarousel: React.FC<BaggageCarouselProps> = ({ flights, totalRows, carouselName, announcement }) => {
    const [tick, setTick] = useState(0);
    const [hours, setHours] = useState(moment().utcOffset(8).format('HH'));
    const [minutes, setMinutes] = useState(moment().utcOffset(8).format('mm'));
    const [dateStr, setDateStr] = useState(moment().utcOffset(8).format('ddd, DD MMM'));

    useEffect(() => {
        const interval = setInterval(() => {
            setTick(prev => prev + 1);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = moment().utcOffset(8);
            setHours(now.format('HH'));
            setMinutes(now.format('mm'));
            setDateStr(now.format('ddd, DD MMM'));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const rows: (FlightDetails | null)[] = [
        ...flights,
        ...Array(Math.max(0, totalRows - flights.length)).fill(null),
    ];

    return (
        <div className="flight-board-container" style={{ '--total-rows': totalRows } as React.CSSProperties}>

            {/* Header */}
            <div className="bc-header">
                <span className="bc-header-carousel">
                    <span className="bc-header-carousel-label">CAROUSEL</span>
                    {' '}
                    <span className="bc-header-carousel-bold">{(carouselName ?? '').trim().toUpperCase()}</span>
                </span>
                <span className="bc-header-date">{dateStr}</span>
            </div>

            {rows.map((flight, index) => (
                <BaggageRow
                    key={flight ? (flight._id as string) : `empty-${index}`}
                    flight={flight}
                    index={index}
                    tick={tick}
                />
            ))}

            {/* Footer */}
            <div className="bc-footer">
                {announcement?.messageContent?.lines?.length ? (
                    <div className="bc-footer-marquee">
                        <ScrollingText
                            sentences={announcement.messageContent.lines}
                            speed={announcement.messageContent.speed || 50}
                            separator=" &bull; "
                            backgroundColor={announcement.messageContent.backgroundColor ?? 'transparent'}
                            textColor={announcement.messageContent.textColor ?? 'white'}
                            fontFamily={announcement.messageContent.fontFamily ?? 'Arial, sans-serif'}
                            fontSize={announcement.messageContent.fontSize ?? 16}
                            noScroll={announcement.messageContent.noScroll ?? false}
                        />
                    </div>
                ) : (
                    <div className="bc-footer-marquee" />
                )}
                <div className="bc-footer-divider" />
                <div className="bc-footer-logo">
                    <img src={mahbLogo} alt="MAHB Logo" className="bc-footer-logo-img" />
                </div>
            </div>
        </div>
    );
};

export default BaggageCarousel;